import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { fmtHMS } from '@foodstyles/shared';
import { useNow } from '../context/NowContext';
import { useEntries } from '../hooks/useEntries';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

const DEFAULT_TITLE = 'FoodStyles — Time Tracker';

function setFavicon(href: string) {
  const link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
  if (link && !link.href.endsWith(href)) link.href = href;
}

// Clockify-style live tab title + green-dot favicon while a timer runs — works
// on every screen and on pinned tabs (where only the favicon is visible).
function TabTitle() {
  const now = useNow();
  const { data } = useEntries();
  const running = data?.running ?? null;
  const isRunning = !!running;

  useEffect(() => {
    if (!running) {
      document.title = DEFAULT_TITLE;
      return;
    }
    const desc = running.description.trim();
    const label = desc.length > 40 ? `${desc.slice(0, 40)}…` : desc || 'Tracking';
    document.title = `${fmtHMS(now - running.start)} · ${label} — FoodStyles`;
  }, [running, now]);

  useEffect(() => {
    setFavicon(isRunning ? '/favicon-running.svg' : '/favicon.svg');
  }, [isRunning]);

  useEffect(
    () => () => {
      document.title = DEFAULT_TITLE;
      setFavicon('/favicon.svg');
    },
    [],
  );

  return null;
}

const ROUTE_META: Record<string, { title: string; sub: string }> = {
  '/tracker': { title: 'Tracker', sub: 'Track time against projects and tasks' },
  '/dashboard': { title: 'Dashboard', sub: "Hours, breakdowns and who's active right now" },
  '/reports': { title: 'Reports', sub: 'Summaries, detailed logs and exports' },
  '/timeoff': { title: 'Time Off', sub: 'Request and approve days off' },
  '/manage': { title: 'Manage', sub: 'Teams, projects and tasks' },
};

export function AppLayout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const meta = ROUTE_META[pathname] ?? { title: 'FoodStyles', sub: '' };
  return (
    <div className="flex h-screen overflow-hidden bg-[#F6F7F9] text-[#1F2430] dark:bg-slate-900 dark:text-slate-100">
      <TabTitle />
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar title={meta.title} sub={meta.sub} />
        <main className="flex-1 overflow-y-auto px-4 py-6 sm:px-7">{children}</main>
      </div>
    </div>
  );
}
